import {AudioTrack, VideoTrack} from '@app/modules/wrtc/models/twilio/types';
import {RemoteAudioTrackPublication, RemoteDataTrackPublication, RemoteVideoTrackPublication} from 'twilio-video';
import {LogService} from '@app/services/log.service';
import {AccessType, LogLevel, LogResource} from '@app/modules/shared/data/constants';

export class RemoteUserTracks {
  public audio: AudioTrack<RemoteAudioTrackPublication>;
  public video: VideoTrack<RemoteVideoTrackPublication>;
  private userId: number;
  private accessType: AccessType;

  constructor() {
    this.audio = new AudioTrack();
    this.video = new VideoTrack();
  }

  public setAudioTrack(trackPublication: RemoteAudioTrackPublication | null, doSubscribe, speakerDeviceId?: string) {
    LogService.writeLog(`Try to set  - ${this.userId} audio track`, LogLevel.DEBUG, LogResource.WRTC);
    if (trackPublication) {
      this.audio.isProvided = true;
      this.audio.trackPublication = trackPublication;
      this.audio.isMuted = !this.audio.trackPublication.isTrackEnabled;

      if (this.audio.isLocalUserSubscribed !== false && doSubscribe) {
        this.subscribeToAudio(true, speakerDeviceId);
      }

    } else {
      this.audio.isProvided = false;
      this.audio.isMuted = false;
      this.audio.trackPublication = null;
    }
  }

  public removeAudioTrack() {
    LogService.writeLog(`Try to remove remote user - ${this.userId} audio track`, LogLevel.DEBUG, LogResource.WRTC);
    if (this.audio.trackPublication && this.audio.trackPublication.track) {
      LogService.writeLog(`Detach ${this.userId} audio track.`, LogLevel.DEBUG, LogResource.WRTC);
      this.audio.trackPublication.track.detach();
    }

    this.audio.trackPublication = null;
    this.audio.isTrackAttached = false;
    this.audio.isProvided = false;
    // this.audio.isMuted = null;
    this.audio.domElement = null;

  }

  public setVideoTrack(trackPublication: RemoteVideoTrackPublication | null) {
    if (trackPublication) {
      LogService.writeLog(`Set remote user - ${this.userId} video track`, LogLevel.DEBUG, LogResource.WRTC);
      this.video.isProvided = true;
      this.video.trackPublication = trackPublication;
      this.video.isMuted = !trackPublication.isTrackEnabled;
    } else {
      LogService.writeLog(`Remote user - ${this.userId} video track has't been set.`, LogLevel.DEBUG, LogResource.WRTC);
      this.video.isProvided = false;
      this.video.isMuted = false;
      this.video.trackPublication = null;
    }
  }

  public removeVideoTrack() {
    LogService.writeLog(`Try to remote user - ${this.userId} video track`, LogLevel.DEBUG, LogResource.WRTC);
    if (this.video.trackPublication && this.video.trackPublication.track) {
      this.video.trackPublication.track.detach();
      LogService.writeLog(`Detach ${this.userId} video track.`, LogLevel.DEBUG, LogResource.WRTC);
    }

    if (this.video.mediaWrapper && this.video.mediaWrapper.querySelector('video')) {
      LogService.writeLog(`Remove ${this.userId} video element wrapper.`, LogLevel.DEBUG, LogResource.WRTC);
      this.video.mediaWrapper.querySelector('video').remove();
    }


    this.video.trackPublication = null;
    this.video.isProvided = false;
    this.video.isMuted = false;
    this.video.isTrackAttached = false;
  }

  public subscribeToAudio(subscribe: boolean, speakerDeviceId?: string) {
    LogService.writeLog(`Try to ${subscribe ? 'subscribe to ' : 'unsubscribe from '} remote user - ${this.userId} audio track`,
      LogLevel.DEBUG, LogResource.WRTC);
    if (this.audio.isProvided) {
      if (subscribe) {
        if (!this.audio.isTrackAttached) {
          LogService.writeLog(`Attach remote user - ${this.userId} audio track`, LogLevel.DEBUG, LogResource.WRTC);
          this.audio.domElement = this.audio.trackPublication.track.attach();
          this.audio.domElement.volume = 0.5;
          this.audio.isTrackAttached = true;
          if (this.accessType === AccessType.STUDENT) {
            this.audio.domElement.volume = this.audio.voiceLevel;
          }

          if (speakerDeviceId) {
            this.audio.domElement.setSinkId(speakerDeviceId).then();
          }
        }
      } else {
        if (this.audio.isTrackAttached) {
          LogService.writeLog(`Detach remote user - ${this.userId} audio track`, LogLevel.DEBUG, LogResource.WRTC);
          this.audio.domElement.volume = 0;
          this.audio.trackPublication.track.detach();
          this.audio.domElement = null;
          this.audio.isTrackAttached = false;
        }
      }
    }
    this.audio.isLocalUserSubscribed = subscribe;
  }

  public setAudioLevel(voiceLevel: number) {
    this.audio.voiceLevel = voiceLevel;
    if (this.audio.domElement) {
      this.audio.domElement.volume = voiceLevel;
    } else {
      LogService.writeLog(`can't get dom element`, LogLevel.WARN, LogResource.WRTC);
    }
  }

  public subscribeToVideo(subscribe: boolean) {
    LogService.writeLog(`Try to ${subscribe ? 'subscribe to' : 'unsubscribe from'} remote user - ${this.userId} video track`,
      LogLevel.DEBUG, LogResource.WRTC);
    if (this.video.isProvided) {
      if (subscribe) {
        if (!this.video.isTrackAttached) {
          if (this.video.mediaWrapper) {
            LogService.writeLog(`Attach remote user - ${this.userId} video track`, LogLevel.DEBUG, LogResource.WRTC);
            const videoElement: HTMLMediaElement = this.video.trackPublication.track.attach();
            videoElement.muted = true;
            videoElement.play().then();
            this.video.mediaWrapper.prepend(videoElement);
            this.video.isTrackAttached = true;
          } else {
            LogService.writeLog(`can't attach video track - media wrapper does not exist.`, LogLevel.WARN, LogResource.WRTC);
          }
        }
      } else {
        if (this.video.isTrackAttached) {
          LogService.writeLog(`Detach remote user - ${this.userId} video track`, LogLevel.DEBUG, LogResource.WRTC);
          this.video.trackPublication.track.detach();
          this.video.mediaWrapper.innerHTML = '';
          this.audio.isTrackAttached = false;
        }
      }
    }
  }

  public setUserId(userId: number) {
    this.userId = userId;
  }

  public setAccessType(accessType: AccessType) {
    this.accessType = accessType;
  }

  public setMediaWrapper(value: HTMLElement) {
    LogService.writeLog(`Set remote user - ${this.userId} video media wrapper`, LogLevel.DEBUG, LogResource.WRTC);
    this.video.mediaWrapper = value;
    // THIS IS IN CASE WHEN MEDIA WRAPPER IS SET AFTER TRACK RECEIVED
    if (this.video.trackPublication && this.video.isProvided && !this.video.isMuted && !this.video.isTrackAttached) {
      LogService.writeLog(`Attach  remote user - ${this.userId} video track after wrapper is Set`, LogLevel.DEBUG, LogResource.WRTC);
      const videoElement = this.video.trackPublication.track.attach();
      this.video.mediaWrapper.prepend(videoElement);
      this.video.isTrackAttached = true;
    }

    // if (this.video.trackPublication && this.video.trackPublication.track) {
    //   const videoElement = this.video.trackPublication.track.attach();
    //   this.mediaWrapper.prepend(videoElement);
    // }
  }

  public removeMediaWrapper() {
    LogService.writeLog(`Remove remote user - ${this.userId} video media wrapper`, LogLevel.DEBUG, LogResource.WRTC);
    if (this.video.trackPublication && this.video.trackPublication.track) {
      this.video.trackPublication.track.detach();
    }

    this.video.mediaWrapper = null;
  }
}
